import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';

import { Options } from '@popperjs/core/lib/popper-lite';
import offsetModifier from '@popperjs/core/lib/modifiers/offset';
import arrowModifier from '@popperjs/core/lib/modifiers/arrow';
import flipModifier from '@popperjs/core/lib/modifiers/flip';

import type { Props } from './types';
import { usePopper } from './usePopper';
import { Arrow, Container, Wrapper, Inner, ButtonWrapper } from './styles';
import { getRootPopup } from './utils';
import { viewModalOnMobile } from './modifiers';

import { isBrowser, isMobile } from '../../utils';
import { Button } from '../../common/Button';
import { IconSvg } from '../../common/IconSvg';

export const Tooltip = React.forwardRef((props: Props, ref) => {
  const {
    position = 'top',
    fallbackPlacements = ['top', 'bottom', 'right', 'left'],
    on = 'click',
    isArrow = true,
    width = 'auto',
    padding = [15, 20],
    offset = 18,
    offsetSkidding = 0,
    duration = 300,
    onOpen,
    onClose,
    children,
    isClosable,
    targetRef,
    ...other
  } = props;

  const config: Options = {
    placement: position,
    strategy: 'fixed',
    modifiers: [
      offsetModifier,
      arrowModifier,
      flipModifier,
      viewModalOnMobile,
      {
        name: 'offset',
        options: {
          offset: [offsetSkidding, offset]
        }
      },
      {
        name: 'arrow',
        options: {
          padding: 20
        }
      },
      {
        name: 'flip',
        options: {
          padding: 0,
          fallbackPlacements: fallbackPlacements || undefined
        }
      }
    ]
  };

  const {
    triggerRef,
    popupRef,
    innerRef,
    isOpen,
    showHandler,
    hideHandler,
    create,
    destroy
  } = usePopper(config, [children, width], targetRef);

  const onEnter = () => {
    create();
    onOpen && onOpen();
  };

  const triggerProps: DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> = {
    ref: triggerRef
  };

  if (on === 'click' || isMobile()) {
    triggerProps.onClick = isOpen ? hideHandler : showHandler;
  }

  if (on === 'hover' && !isMobile()) {
    triggerProps.onMouseEnter = showHandler;
    triggerProps.onMouseLeave = hideHandler;
  }

  return (
    <>
      {React.cloneElement(props.trigger, triggerProps)}

      {isBrowser &&
        ReactDOM.createPortal(
          <CSSTransition
            unmountOnExit={true}
            in={isOpen}
            timeout={duration}
            onEnter={onEnter}
            onExited={destroy}
            onExit={onClose}
            classNames="tooltip"
          >
            <Container ref={popupRef} isOpen={isOpen} duration={duration} role="tooltip" {...other}>
              <Wrapper ref={innerRef} isOpen={isOpen} duration={duration} width={width}>
                <Inner ref={ref as React.RefObject<HTMLDivElement>} padding={padding}>
                  {isClosable && (
                    <ButtonWrapper>
                      <Button size="S" view="transparent" shape="circle" onClick={hideHandler}>
                        <IconSvg src="close" />
                      </Button>
                    </ButtonWrapper>
                  )}
                  {children && typeof children === 'function' ? children(hideHandler) : children}
                </Inner>
                {<Arrow isVisible={isArrow} data-popper-arrow></Arrow>}
              </Wrapper>
            </Container>
          </CSSTransition>,
          getRootPopup()
        )}
    </>
  );
});
